import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import Button from '@zenbusiness/zui/Button';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import EmptyPayment from '#client/components/EmptyPayment';
import FilterOptions from '#client/components/FilterOptions';
import MobileDrawer from '#client/components/MobileDrawer';
import NoResultTransaction from '#client/components/NoResultTransaction';
import SelectInput from '#client/components/SelectInput';
import TransactionSkeleton from '#client/components/TransactionListSkeleton';
import { PAGINATION_DEFAULT_LIMIT } from '#client/constants/pagination';
import {
  PAYMENT_LIST_STATUS,
  RENDER_TRANSACTION_TYPE,
  statusOptions
} from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_LIST_PAYMENTS_QUERY } from '#client/queries';
import { getDateRange, getMonthSelectOptions } from '#client/utils/date';
import { getOptionName } from '#client/utils/transaction';

import ListPaymentTransaction from './components/ListPaymentTransaction';
import { OptionButtonStyle, PaymentContainerStyled } from './PaymentContainer.styles';

const MONTH = 'month';
const STATUS = 'status';

const initFilter = {
  [MONTH]: '',
  [STATUS]: ''
};

const monthOptions = getMonthSelectOptions();

export default function PaymentContainer({ emptyText, callback = () => {}, counterparty }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const [showMonthFilter, setShowMonthFilter] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);

  const [filters, setFilters] = useState(initFilter);
  const [currentPage, setCurrentPage] = useState(1);
  const [isClickLoadMore, setIsClickLoadMore] = useState(false);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { createCommonErrorToast } = useToastContext();

  const updateOriginalTransactions = (prevData, data) => {
    const paymentList = isClickLoadMore
      ? [...prevData.bankingPayments.bankingPayments, ...data.bankingPayments.bankingPayments]
      : data.bankingPayments.bankingPayments;
    setIsClickLoadMore(false);

    return {
      bankingPayments: {
        bankingPayments: paymentList,
        pageInfo: data.bankingPayments.pageInfo
      }
    };
  };

  const {
    loading,
    error: paymentError,
    errors,
    data: originalData,
    refetch
  } = useQuery(GET_LIST_PAYMENTS_QUERY, {
    variables: {
      filter: {
        businessEntityUuid,
        counterpartyId: counterparty.counterpartyId,
        status: filters[STATUS] === '' || filters[STATUS] === 'all' ? undefined : filters[STATUS],
        month:
          filters[MONTH] === '' || filters[MONTH] === 'all'
            ? undefined
            : getDateRange(filters[MONTH])
      },
      page: {
        pageSize: PAGINATION_DEFAULT_LIMIT,
        page: currentPage
      }
    },
    skipCache: true,
    skip: !unitAccountId,
    noThrow: true,
    updateData: updateOriginalTransactions
  });

  useLogError(errors, paymentError);
  const data = useMemoObject(originalData);

  useEffect(() => {
    if (paymentError) {
      createCommonErrorToast({ error: paymentError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentError]);

  const payments = useMemo(() => data?.bankingPayments?.bankingPayments ?? [], [data]);

  const hasMore = useMemo(
    () => !!data?.bankingPayments?.pageInfo?.hasNextPage && payments.length > 0,
    [data?.bankingPayments?.pageInfo?.hasNextPage, payments.length]
  );

  const isHideSkeleton = (data && !loading) || paymentError;
  const [showSkeleton, setShowSkeleton] = useSkeleton(isHideSkeleton);

  const handleFilterCommon = useCallback(() => {
    setCurrentPage(1);
    setShowSkeleton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeFilters = (filter, value) => {
    setFilters((prev) => ({
      ...prev,
      [filter]: value
    }));
  };

  const handleDesktopFilterChange = (filter) => (event) => {
    if (!(filters[filter] === '' && event.target.value === 'all')) {
      handleFilterCommon();
    }

    handleChangeFilters(filter, event.target.value);
  };

  const handleMobileFilterChange = (filter) => (key) => {
    if (!(filters[filter] === '' && key === 'all')) {
      handleFilterCommon();
    }
    handleChangeFilters(filter, key);
  };

  const handleLoadMore = useCallback(() => {
    setIsClickLoadMore(true);
    setCurrentPage((prevPage) => prevPage + 1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(initFilter);
    handleFilterCommon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isEmptyFilter = !filters[STATUS] && !filters[MONTH];
    const isEmptyPayment = !loading && payments.length === 0 && isEmptyFilter;

    if (!data) {
      callback(PAYMENT_LIST_STATUS.LOADING);
    }

    return isEmptyPayment
      ? callback(PAYMENT_LIST_STATUS.EMPTY)
      : callback(PAYMENT_LIST_STATUS.HAS_PAYMENT);
  }, [callback, data, filters, loading, payments.length]);

  /**
   * Function is used for transaction list get from api is empty,
   * not found transaction or render a list transaction
   */
  const renderPaymentType = useMemo(() => {
    // The transaction is empty when no transaction return and all filters are empty
    const isEmptyPayment = payments.length === 0 && !filters[STATUS] && !filters[MONTH];

    // The transaction is empty when no transaction return and at least one filter has value
    const isNotFoundTransaction = payments.length === 0 && (!!filters[STATUS] || !!filters[MONTH]);

    let renderType = RENDER_TRANSACTION_TYPE.SHOW_LIST;

    if (isEmptyPayment) {
      renderType = RENDER_TRANSACTION_TYPE.EMPTY;
    }

    if (isNotFoundTransaction) {
      renderType = RENDER_TRANSACTION_TYPE.NOT_FOUND;
    }

    return renderType;
  }, [filters, payments]);

  const isDisabledFilter =
    renderPaymentType === RENDER_TRANSACTION_TYPE.EMPTY || (loading && !isClickLoadMore);

  const listPayment = useMemo(() => {
    switch (renderPaymentType) {
      case RENDER_TRANSACTION_TYPE.EMPTY:
        return <EmptyPayment description={emptyText} />;
      case RENDER_TRANSACTION_TYPE.NOT_FOUND:
        return <NoResultTransaction handleReset={handleResetFilters} />;
      default:
        return (
          <ListPaymentTransaction
            counterparty={counterparty}
            handleLoadMore={handleLoadMore}
            hasMore={hasMore}
            isLoading={loading}
            refetchPayment={refetch}
            transactions={payments}
          />
        );
    }
  }, [
    renderPaymentType,
    emptyText,
    handleResetFilters,
    payments,
    handleLoadMore,
    loading,
    hasMore,
    refetch,
    counterparty
  ]);

  const handleCloseMonthFilterMobile = () => setShowMonthFilter(false);
  const handleCloseStatusFilterMobile = () => setShowStatusFilter(false);
  return (
    <>
      <PaymentContainerStyled maxWidth="xl">
        <Stack
          marginBottom="24px"
          spacing="16px"
        >
          {isDesktop ? (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
            >
              <Stack
                alignItems="center"
                direction="row"
                spacing="8px"
              >
                <SelectInput
                  bordered
                  disabled={isDisabledFilter}
                  handleChange={handleDesktopFilterChange(MONTH)}
                  Id="month"
                  label="Month"
                  minWidth={100}
                  options={monthOptions}
                  value={filters[MONTH]}
                />
                <SelectInput
                  bordered
                  disabled={isDisabledFilter}
                  handleChange={handleDesktopFilterChange(STATUS)}
                  Id="type"
                  label="Status"
                  minWidth={100}
                  options={statusOptions}
                  value={filters[STATUS]}
                />
              </Stack>
            </Stack>
          ) : (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
            >
              <Stack
                alignItems="center"
                direction="row"
                spacing="8px"
              >
                <Button
                  disabled={isDisabledFilter}
                  endIcon={
                    <SharedAsset
                      alt=""
                      src="/s2/icon/arrow-sm-down-outline.svg"
                    />
                  }
                  onClick={() => setShowMonthFilter(true)}
                  sx={OptionButtonStyle}
                  variant="outlined"
                >
                  {getOptionName(filters[MONTH], monthOptions) ?? 'Month'}
                </Button>
                <Button
                  disabled={isDisabledFilter}
                  endIcon={
                    <SharedAsset
                      alt=""
                      src="/s2/icon/arrow-sm-down-outline.svg"
                    />
                  }
                  onClick={() => setShowStatusFilter(true)}
                  sx={OptionButtonStyle}
                  variant="outlined"
                >
                  {getOptionName(filters[STATUS], statusOptions) ?? 'Status'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
        {showSkeleton ? <TransactionSkeleton /> : listPayment}
      </PaymentContainerStyled>
      {showMonthFilter && (
        <MobileDrawer handleClose={handleCloseMonthFilterMobile}>
          <FilterOptions
            callback={handleCloseMonthFilterMobile}
            handleClick={handleMobileFilterChange(MONTH)}
            options={monthOptions}
            selectedKey={filters[MONTH]}
          />
        </MobileDrawer>
      )}
      {showStatusFilter && (
        <MobileDrawer handleClose={handleCloseStatusFilterMobile}>
          <FilterOptions
            callback={handleCloseStatusFilterMobile}
            handleClick={handleMobileFilterChange(STATUS)}
            options={statusOptions}
            selectedKey={filters[STATUS]}
          />
        </MobileDrawer>
      )}
    </>
  );
}

PaymentContainer.propTypes = {
  emptyText: PropTypes.string.isRequired,
  counterparty: PropTypes.shape({
    counterpartyId: PropTypes.string,
    bank: PropTypes.string,
    connectedType: PropTypes.string,
    accountNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string
  }),
  callback: PropTypes.func
};
